import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BriefcaseIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
export function HeroSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  return <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-navy-50" data-id="element-285">
      <div className="absolute inset-0 overflow-hidden" data-id="element-286">
        <div className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full bg-primary-200/25 blur-3xl" data-id="element-287" />
        <div className="absolute top-1/2 -left-20 w-[500px] h-[500px] rounded-full bg-primary-300/15 blur-3xl" data-id="element-288" />
        <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] rounded-full bg-cyan-200/20 blur-3xl" data-id="element-289" />
        <div className="absolute top-1/4 right-1/4 w-[300px] h-[300px] rounded-full bg-indigo-200/10 blur-3xl" data-id="element-290" />
        <div className="absolute inset-0 opacity-[0.025]" style={{
        backgroundImage: `linear-gradient(rgba(15,23,42,1) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} data-id="element-291" />
        <motion.div animate={{
        y: [0, -20, 0],
        rotate: [0, 5, 0]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-32 right-[15%] w-16 h-16 border-2 border-primary-300/30 rounded-2xl" data-id="element-292" />
        <motion.div animate={{
        y: [0, 15, 0],
        rotate: [0, -8, 0]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 1
      }} className="absolute bottom-40 left-[10%] w-12 h-12 bg-primary-400/10 rounded-full" data-id="element-293" />
        <motion.div animate={{
        y: [0, -12, 0]
      }} transition={{
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 2
      }} className="absolute top-1/2 right-[8%] w-8 h-8 border-2 border-cyan-300/25 rounded-lg rotate-45" data-id="element-294" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-40 w-full" data-id="element-295">
        <div className="max-w-3xl" data-id="element-296">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-297">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-semibold text-primary-700 bg-primary-100/80 rounded-full border border-primary-200/60 shadow-sm" data-id="element-298">
              <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" data-id="element-299" />
              {t.hero.badge}
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-navy-900 tracking-tight leading-[1.06] text-balance" data-id="element-300">
            {t.hero.h1_1}{' '}
            <span className="relative inline-block" data-id="element-301">
              <span className="relative z-10 text-primary-600" data-id="element-302">
                {t.hero.h1_accent}
              </span>
              <motion.span initial={{
              scaleX: 0
            }} animate={{
              scaleX: 1
            }} transition={{
              duration: 0.6,
              delay: 0.75
            }} className="absolute bottom-1 left-0 right-0 h-3 bg-primary-200/60 -z-0 origin-left rounded-sm" data-id="element-303" />
            </span>{' '}
            {t.hero.h1_2}
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.25
        }} className="mt-6 text-lg sm:text-xl text-navy-500 leading-relaxed max-w-2xl" data-id="element-304">
            {t.hero.subtitle}
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="mt-10 flex flex-col sm:flex-row gap-4" data-id="element-305">
            <a href="#contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 text-base font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-600/30 hover:shadow-xl hover:shadow-primary-600/35 hover:-translate-y-0.5" data-id="element-306">
              {t.hero.cta_primary}
              <ArrowRightIcon className="w-4 h-4" data-id="element-307" />
            </a>
            <a href="#niches" className="inline-flex items-center justify-center gap-2 px-7 py-4 text-base font-semibold text-navy-700 bg-white rounded-xl hover:bg-navy-50 transition-all border border-navy-200 hover:-translate-y-0.5 shadow-sm" data-id="element-308">
              <BriefcaseIcon className="w-4 h-4" data-id="element-309" />
              {t.hero.cta_secondary}
            </a>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.55
        }} className="mt-14 grid grid-cols-3 gap-8 max-w-lg" data-id="element-310">
            <div data-id="element-311">
              <div className="text-2xl sm:text-3xl font-extrabold text-navy-900" data-id="element-312">
                {t.hero.stat1_val}
              </div>
              <div className="text-xs text-navy-500 font-medium mt-0.5" data-id="element-313">
                {t.hero.stat1_label}
              </div>
            </div>
            <div data-id="element-314">
              <div className="text-2xl sm:text-3xl font-extrabold text-navy-900" data-id="element-315">
                {t.hero.stat2_val}
              </div>
              <div className="text-xs text-navy-500 font-medium mt-0.5" data-id="element-316">
                {t.hero.stat2_label}
              </div>
            </div>
            <div data-id="element-317">
              <div className="text-2xl sm:text-3xl font-extrabold text-navy-900" data-id="element-318">
                {t.hero.stat3_val}
              </div>
              <div className="text-xs text-navy-500 font-medium mt-0.5" data-id="element-319">
                {t.hero.stat3_label}
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.7
        }} className="mt-8 flex items-center gap-3 text-sm text-navy-400" data-id="element-320">
            <div className="flex -space-x-2" data-id="element-321">
              {['bg-primary-500', 'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500'].map((bg, i) => <div key={i} className={`w-8 h-8 rounded-full ${bg} border-2 border-navy-50 flex items-center justify-center text-white text-xs font-bold`} data-id="element-322">
                  {['А', 'Б', 'В', 'Г'][i]}
                </div>)}
            </div>
            <span data-id="element-323">
              <strong className="text-navy-700" data-id="element-324">500+</strong> {t.hero.trust}
            </span>
          </motion.div>
        </div>
      </div>
    </section>;
}